/**
 * SlotConflictPage.js (Part B)
 *
 * The dedicated page behind the slot-conflict SMS/email link.
 *
 * Scenario: the customer paid for a time slot that somebody else took while their
 * payment was processing. Their money is safe, but they need a time. This page is
 * purpose-built for exactly that, and is reached with a ready-made reschedule
 * token so they never re-enter a booking number, email or phone digits.
 *
 * Deliberately NOT the generic reschedule page:
 *   - it explains WHY the time changed
 *   - it shows the time we already held for them, if we found one
 *   - there is NO charge to fix a conflict that was not their fault
 *
 * Uses the existing reschedule preview + confirm endpoints, which accept a
 * `slot_conflict` booking and waive the late-reschedule fee for it.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import "./BookingConfirmation.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const toYmd = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatLongDate = (ymd, timezone) => {
  if (!ymd) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(new Date(`${ymd}T12:00:00`));
  } catch (err) {
    return String(ymd);
  }
};

const formatClock = (value) => {
  if (!value) return "";
  const [hours, minutes] = String(value).split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return String(value);
  const period = hours >= 12 ? "PM" : "AM";
  return `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${period}`;
};

const SlotConflictPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [booking, setBooking] = useState(null);
  const [policy, setPolicy] = useState(null);

  const [newDate, setNewDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const conflict = booking?.slotConflict || null;

  // The whole group occupies one continuous block, so availability must be asked
  // for the full chain or the customer is offered a start that cannot actually fit.
  const durationForSlots = useMemo(
    () => Number(booking?.groupDurationMinutes) || Number(booking?.serviceDuration) || 0,
    [booking],
  );

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      if (!token) {
        setError(
          "This link is missing its secure token. Open the link from your email or text message, or use My Bookings.",
        );
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/bookings/reschedule/preview?token=${encodeURIComponent(token)}`,
        );
        const data = await response.json().catch(() => ({}));

        if (cancelled) return;

        if (!response.ok) {
          setError(
            data.message ||
              "This link is no longer valid. Open the link from your email, or use My Bookings.",
          );
          setBooking(null);
          return;
        }

        setBooking(data.booking || null);
        setPolicy(data.policy || null);

        // Pre-select the day we already held for them, so the offered time is the
        // first thing they see rather than an empty calendar.
        const heldDate = data.booking?.slotConflict?.proposedDate || data.booking?.appointmentDate;
        const heldStart = data.booking?.slotConflict?.proposedStartTime || data.booking?.startTime;
        if (heldDate) setNewDate(new Date(`${heldDate}T12:00:00`));
        if (heldStart) setSelectedSlot(heldStart);
      } catch (err) {
        if (!cancelled) {
          setError("Unable to load this appointment. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    let cancelled = false;

    const loadSlots = async () => {
      const dateYmd = toYmd(newDate);
      if (!booking?.serviceId || !booking?.staffId || !dateYmd || !durationForSlots || success) {
        setSlots([]);
        return;
      }

      setSlotsLoading(true);
      setSlotsError("");

      try {
        const params = new URLSearchParams({
          date: dateYmd,
          serviceId: booking.serviceId,
          staffId: booking.staffId,
          serviceDuration: String(durationForSlots),
        });
        const response = await fetch(`${API_BASE_URL}/api/bookings/available-slots?${params}`);
        const data = await response.json().catch(() => ({}));

        if (cancelled) return;

        if (!response.ok) {
          throw new Error(data.message || "Unable to load available times.");
        }

        setSlots(Array.isArray(data.availableSlots) ? data.availableSlots : []);
      } catch (err) {
        if (!cancelled) {
          setSlotsError(err.message || "Unable to load available times.");
          setSlots([]);
        }
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    };

    loadSlots();
    return () => {
      cancelled = true;
    };
  }, [booking, durationForSlots, newDate, success]);

  const submitReschedule = async () => {
    const dateYmd = toYmd(newDate);
    if (!dateYmd || !selectedSlot) {
      setError("Choose a date and a time first.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/bookings/reschedule/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          newDate: dateYmd,
          newStartTime: selectedSlot,
          reason: "Customer chose a new time after a slot conflict",
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.message ||
            "That time could not be booked. Please choose another time and try again.",
        );
      }

      setSuccess({
        appointmentDate: data.booking?.appointmentDate || dateYmd,
        startTime: data.booking?.startTime || selectedSlot,
      });
    } catch (err) {
      setError(err.message || "Unable to book that time. Please try another.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div className="confirmation-container">
          <div className="confirmation-loading">
            <div className="spinner" />
            <p>Loading your appointment...</p>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (success) {
    return (
      <>
        <CustomerHeader />
        <div className="confirmation-container">
          <div className="confirmation-success-header">
            <div className="success-icon">✅</div>
            <h1>Your new time is booked</h1>
            <p className="success-message">
              {formatLongDate(success.appointmentDate)} at {formatClock(success.startTime)}
            </p>
          </div>
          <div className="booking-details">
            <p style={{ color: "#374151", fontSize: "15px", margin: "0 0 16px" }}>
              Nothing more to pay &mdash; your original payment covers this appointment. You will
              get a confirmation by email.
            </p>
            <div className="confirmation-actions">
              <Link className="btn-primary" to="/customer/dashboard/appointments">
                View my bookings
              </Link>
            </div>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (error && !booking) {
    return (
      <>
        <CustomerHeader />
        <div className="confirmation-container">
          <div className="confirmation-error">
            <div className="error-icon">⚠️</div>
            <h2>We could not open that link</h2>
            <p>{error}</p>
            <Link className="btn-primary" to="/customer/dashboard/appointments">
              Go to my bookings
            </Link>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  const heldLabel = conflict?.proposedDate
    ? `${formatLongDate(conflict.proposedDate)} at ${formatClock(conflict.proposedStartTime)}`
    : null;
  const paidLabel = conflict?.originalDate
    ? `${formatLongDate(conflict.originalDate)} at ${formatClock(conflict.originalStartTime)}`
    : null;

  return (
    <>
      <CustomerHeader />
      <div className="confirmation-container">
        <div className="confirmation-success-header" style={{ background: "#1a659e" }}>
          <h1>Choose a new time</h1>
          <p className="success-message">
            There is no charge to change this appointment.
          </p>
        </div>

        <div className="booking-details">
          <div
            style={{
              borderLeft: "5px solid #1a659e",
              background: "#f0f9ff",
              borderRadius: "10px",
              padding: "16px 18px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: "0 0 10px", color: "#374151", fontSize: "15px" }}>
              Someone else booked{" "}
              <strong>{paidLabel || "the time you paid for"}</strong> while your payment was
              processing. Your payment went through, so we just need to give you a new time with
              the same professional.
            </p>
            {heldLabel && (
              <p style={{ margin: 0, color: "#0f172a", fontSize: "15px" }}>
                We have already held <strong>{heldLabel}</strong> for you &mdash; keep it below,
                or pick something else.
              </p>
            )}
          </div>

          <div style={{ marginBottom: "24px" }}>
            <h3 style={{ margin: "0 0 10px", fontSize: "16px", color: "#111827" }}>
              Pick a date
            </h3>
            <Calendar
              value={newDate}
              onChange={(value) => {
                setNewDate(value);
                setSelectedSlot("");
              }}
              minDate={new Date()}
              maxDate={new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)}
            />
          </div>

          <div style={{ marginBottom: "24px" }}>
            <h3 style={{ margin: "0 0 10px", fontSize: "16px", color: "#111827" }}>
              Available times
            </h3>

            {slotsLoading && <p style={{ color: "#6b7280" }}>Checking availability...</p>}
            {!slotsLoading && slotsError && (
              <p style={{ color: "#b91c1c" }}>{slotsError}</p>
            )}
            {!slotsLoading && !slotsError && slots.length === 0 && (
              <p style={{ color: "#6b7280" }}>
                No times on this day. Please choose another date.
              </p>
            )}
            {!slotsLoading && slots.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {slots.map((slot) => {
                  const isSelected = selectedSlot === slot.time24;
                  return (
                    <button
                      key={slot.time24}
                      type="button"
                      onClick={() => setSelectedSlot(slot.time24)}
                      style={{
                        padding: "10px 16px",
                        borderRadius: "8px",
                        border: `2px solid ${isSelected ? "#1a659e" : "#cbd5e1"}`,
                        background: isSelected ? "#1a659e" : "#ffffff",
                        color: isSelected ? "#ffffff" : "#1a659e",
                        fontWeight: 600,
                        fontSize: "14px",
                        cursor: "pointer",
                      }}
                    >
                      {slot.time12 || formatClock(slot.time24)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {error && (
            <p style={{ color: "#b91c1c", fontWeight: 600, marginBottom: "16px" }}>{error}</p>
          )}

          <div className="confirmation-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={submitReschedule}
              disabled={submitting || !selectedSlot}
            >
              {submitting ? "Booking..." : "Confirm this time"}
            </button>
            <Link className="btn-secondary" to="/refund-service">
              I would rather cancel and get a refund
            </Link>
          </div>

          {policy?.groupBookingCount > 1 && (
            <p style={{ color: "#6b7280", fontSize: "13px", marginTop: "12px" }}>
              This covers all {policy.groupBookingCount} appointments in your booking, kept
              back-to-back with the same professional.
            </p>
          )}
        </div>
      </div>
      <CustomerFooter />
    </>
  );
};

export default SlotConflictPage;
